import type {
  CompressionTable,
  JsonSchema as KeyCompressionJsonSchema,
} from 'jsonschema-key-compression';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  compressedPath,
  createCompressionTable,
  DEFAULT_COMPRESSION_FLAG,
} from 'jsonschema-key-compression';
import { getPrimaryFieldOfPrimaryKey, RxJsonSchema } from 'rxdb';
import { flatClone } from 'rxdb/plugins/utils';

export function getIndexName(index: string | ReadonlyArray<string>) {
  // claude eslint: Array.isArray narrowed the array branch; the else branch is string, but TS doesn't narrow the union fully here
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Array.isArray(index) ? index.join(',') : (index as string);
}

export function getStorageIndexName(index: string | ReadonlyArray<string>) {
  return (Array.isArray(index) ? Array.from(index) : [index]).join('||');
}

export function getFilteredIndexes(
  compression: boolean,
  schema: RxJsonSchema<never>,
  wrappedStorageSchema: RxJsonSchema<never>,
  wrappedStorageIndexes: string[] | string[][],
) {
  const seen = new Set<string>();

  /**
   * Simple case: There is no compression. We can just filter out indices that
   * aren't defined in the list of indices in the uncompressed schema.
   *
   * Complex case: When the schema is compressed, we need to compress the
   * override indices for the wrapped storage ourselves. We can only do that
   * with access to the original uncompressed schema. We use it to create a
   * compression table that returns the same values as present in the
   * compressed schema.
   */
  if (!compression) {
    const wrappedStorageIndexLookup: Set<string> = new Set(wrappedStorageIndexes.map(getIndexName));
    return schema.indexes?.filter((index) => {
      const indexName = getIndexName(index);

      // filter duplicates
      if (seen.has(indexName)) {
        return false;
      } else {
        seen.add(indexName);
      }

      // never filter this index
      if (indexName === '_meta.lwt,id') {
        return true;
      }
      return wrappedStorageIndexLookup.has(getIndexName(index));
    });
  }

  // claude eslint: RxJsonSchema and KeyCompressionJsonSchema are structurally compatible; flatClone returns the same shape but TS can't infer it
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-type-assertion
  const compressionSchema: KeyCompressionJsonSchema = flatClone(wrappedStorageSchema) as any;
  // claude eslint: KeyCompressionJsonSchema doesn't declare primaryKey but we need to delete it before passing to createCompressionTable
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-type-assertion
  delete (compressionSchema as any).primaryKey;

  const table = createCompressionTable(compressionSchema, DEFAULT_COMPRESSION_FLAG, [
    getPrimaryFieldOfPrimaryKey(wrappedStorageSchema.primaryKey),
    '_rev',
    '_attachments',
    '_deleted',
    '_meta',
  ]);

  const wrappedStorageIndexLookup: Set<string> = new Set(
    wrappedStorageIndexes.map((index) => {
      const compressedIndex = compressIndex(table, index);
      return getIndexName(compressedIndex);
    }),
  );

  return schema.indexes?.filter((index) => {
    const parts = Array.isArray(index) ? Array.from(index) : [index];
    const indexName = getIndexName(parts);

    // filter duplicates
    if (seen.has(indexName)) {
      return false;
    } else {
      seen.add(indexName);
    }

    // never filter this index
    if (indexName === '_meta.lwt,id') {
      return true;
    }
    return wrappedStorageIndexLookup.has(indexName);
  });
}

function compressIndex(table: CompressionTable, index: string | ReadonlyArray<string>) {
  if (Array.isArray(index)) {
    // eslint-disable-next-line no-console
    return index.map((subIndex) => compressedPath(table, subIndex));
  } else {
    // claude eslint: Array.isArray narrowed the array branch above; the else branch is string but TS doesn't fully narrow the union
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return compressedPath(table, index as string);
  }
}
